import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { linkify, safelyDetectChanges } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import FieldsConstants from 'src/app/common/fields.constants';
import { ChatMessage } from 'src/app/components/chat/interfaces/chat-model.interface';
import { EditMessageBody } from 'src/app/components/chat/interfaces/chat-operations.interface';
import { DialogMessagePlaceholders } from 'src/app/notifications/dialogs/dialog-message/dialog-message-placeholders.model';
import { ConversationForm } from '../conversation-form.abstract';

@Component({
  selector: 'app-message-form-field',
  templateUrl: '../conversation-form.component.html',
  styleUrls: ['../conversation-form.component.scss'],
})
export class MessageFormFieldComponent extends ConversationForm implements OnChanges {
  @Input({ required: true }) public counterVisibility: boolean;
  @Input({ required: true }) public placeholderPhrases: DialogMessagePlaceholders;

  @Input({ required: false }) public memberTypingName = '';
  @Input({ required: false }) public isReadOnly = false;
  @Input({ required: false }) public userAvatarVisibility = false;
  @Input({ required: false }) public maxLength: number = FieldsConstants.CHAT_MAX_MESSAGE_LENGTH;
  @Input({ required: false }) public messageForEdit: ChatMessage | null;
  @Input({ required: false }) public indexLabel: string;

  @Output() public sendTextMessage: EventEmitter<string> = new EventEmitter<string>();
  @Output() public editMessageFinished: EventEmitter<EditMessageBody> = new EventEmitter<EditMessageBody>();
  @Output() public enteringMessageAborted: EventEmitter<void> = new EventEmitter<void>();
  @Output() public userTyping: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('inputMessage', { static: false }) public inputMessageRef: ElementRef<HTMLElement>;

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges['messageForEdit']) {
      const changes: SimpleChange = simpleChanges['messageForEdit'];
      if (changes.currentValue && changes.currentValue !== changes.previousValue) {
        this.editMessageStartedHandler();
      } else if (changes.previousValue && !changes.currentValue) {
        this.editMessageFinishedHandler();
      }
    }
  }

  public submitMessage(): void {
    if (this.messageInput.value) {
      this.messageInput.patchValue(linkify(this.messageInput.value.trim()));
    }

    this.messageSubmitted = true;

    if (this.messageForm.invalid || !this.messageInput.value) {
      return;
    }

    if (this.messageForEdit) {
      this.editMessageFinished.emit({
        messageId: this.messageForEdit.entity_id,
        messageValue: this.messageInput.value,
      });
    } else {
      this.sendTextMessage.emit(this.messageInput.value);
    }

    this.editMessageFinishedHandler();
    this.dirtyForm.emit(false);
    safelyDetectChanges(this.changeDetectorRef);
  }

  public getValidationError(): string {
    if (!this.messageInput || !this.messageInput.errors) {
      return '';
    }

    if (this.messageInput.errors['pattern']) {
      return this.placeholderPhrases.emptyErrorHint;
    }

    if (this.messageInput.errors['maxlength']) {
      return this.placeholderPhrases.maxLengthErrorHint;
    }

    return '';
  }

  /**
   * @description On press the escape key clears the form.
   * @param { KeyboardEvent } event
   */
  public onKeyUpEvent(event: KeyboardEvent): void {
    this.escapeMessageHandler(event);
    this.userTypingHandler(event);

    safelyDetectChanges(this.changeDetectorRef);
  }

  protected allowSubmittingEmptyMessage(): boolean {
    return false;
  }

  protected getMaxLengthConstraint(): number {
    return this.maxLength;
  }

  private editMessageStartedHandler(): void {
    this.inputMessageRef.nativeElement.focus();

    if (this.messageForEdit) {
      this.messageInput.patchValue(this.messageForEdit.text);
    }
  }

  private editMessageFinishedHandler(): void {
    this.clearInputMessage();
  }

  private clearInputMessage(): void {
    this.messageInput.reset();
    this.messageSubmitted = false;
  }

  private escapeMessageHandler(event: KeyboardEvent): void {
    if (event.code === AppValues.KEYCODE_ESCAPE) {
      this.editMessageFinishedHandler();
      this.enteringMessageAborted.emit();
    }
  }

  private userTypingHandler(event: KeyboardEvent): void {
    if (event.code === AppValues.KEYCODE_ESCAPE || (event.ctrlKey && event.code === AppValues.KEYCODE_ENTER)) {
      return;
    }
    this.userTyping.emit();
  }
}
